import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronRight,
  Clock,
  Edit,
  LayoutGrid,
  List,
  Maximize2,
  MessageSquare,
  MoreVertical,
  Pause,
  Play,
  Radio,
  RefreshCw,
  Trash2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { RoleScopeChips, type StreamDefinitionLabelMaps } from './RoleScopeChips';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader } from '../ui/Card';
import { DropdownMenu } from '../ui/DropdownMenu';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { cn } from '../../lib/utils';
import { streamLeafCardSurface } from '../../lib/colors';
import { getRoleKey, resolveParentKey } from '../../lib/ai-role-hierarchy';
import { type RoleSummary } from './LeafConfigCard';

interface AgentHierarchyViewProps {
  roles: RoleSummary[];
  allRoles?: RoleSummary[];
  definitionLabelMaps?: StreamDefinitionLabelMaps;
  canManage: boolean;
  /** AI Org Setup: chat with role agent (OPA ai_config:chat) */
  canChat?: boolean;
  onChatRole?: (leaf: RoleSummary) => void;
  onEditRole: (leaf: RoleSummary) => void;
  onPauseToggle: (leaf: RoleSummary) => void;
  onDelete: (leaf: RoleSummary) => void;
  onListeners?: (leaf: RoleSummary) => void;
  onSchedules?: (leaf: RoleSummary) => void;
  onReconcile?: (leaf: RoleSummary) => void;
  onSyncRole?: (leaf: RoleSummary) => void;
  onSetParent?: (leaf: RoleSummary, parentRef?: string) => Promise<void>;
  roleActivity?: Record<string, { working: boolean; activeChats: number; activeMissions: number }>;
  syncingRoleKeys?: Set<string>;
  onOpenActiveChats?: (leaf: RoleSummary) => void;
  className?: string;
  /** Default is compact cards (role title, stream, status). */
  initialCardLayout?: 'lite' | 'full';
}

export type HierarchyCardLayout = 'lite' | 'full';

function childIsLeafInTree(childKey: string, childrenByParent: Map<string, string[]>): boolean {
  const next = childrenByParent.get(childKey);
  return !next || next.length === 0;
}

/** Compact grid columns for a row of sibling leaves (cap 4 wide). */
function leafGroupGridColumnCount(n: number): number {
  if (n <= 2) return n;
  return Math.min(4, Math.max(2, Math.ceil(Math.sqrt(n))));
}

function HierarchyNode({
  nodeKey,
  rolesByKey,
  childrenByParent,
  definitionLabelMaps,
  cardLayout,
  canManage,
  canChat,
  onChatRole,
  onEditRole,
  onPauseToggle,
  onDelete,
  onListeners,
  onSchedules,
  onReconcile,
  onSyncRole,
  onSetParent,
  roleActivity,
  syncingRoleKeys,
  onOpenActiveChats,
  externalParentLabel,
}: {
  nodeKey: string;
  rolesByKey: Map<string, RoleSummary>;
  childrenByParent: Map<string, string[]>;
  definitionLabelMaps?: StreamDefinitionLabelMaps;
  cardLayout: HierarchyCardLayout;
  canManage: boolean;
  canChat?: boolean;
  onChatRole?: (leaf: RoleSummary) => void;
  onEditRole: (leaf: RoleSummary) => void;
  onPauseToggle: (leaf: RoleSummary) => void;
  onDelete: (leaf: RoleSummary) => void;
  onListeners?: (leaf: RoleSummary) => void;
  onSchedules?: (leaf: RoleSummary) => void;
  onReconcile?: (leaf: RoleSummary) => void;
  onSyncRole?: (leaf: RoleSummary) => void;
  onSetParent?: (leaf: RoleSummary) => void;
  roleActivity?: Record<string, { working: boolean; activeChats: number; activeMissions: number }>;
  syncingRoleKeys?: Set<string>;
  onOpenActiveChats?: (leaf: RoleSummary) => void;
  externalParentLabel?: string;
}) {
  const leaf = rolesByKey.get(nodeKey);
  if (!leaf) return null;

  const children = childrenByParent.get(nodeKey) || [];
  /** Role has no child roles in this tree — stream accent / violet applies only here, not on parent nodes. */
  const isLeafCard = children.length === 0;

  const allSiblingsAreLeaves =
    children.length > 2 && children.every((k) => childIsLeafInTree(k, childrenByParent));
  const leafGridCols = allSiblingsAreLeaves ? leafGroupGridColumnCount(children.length) : 0;
  const leafSurf = isLeafCard ? streamLeafCardSurface(leaf.streamColor) : null;
  const activity = roleActivity?.[getRoleKey(leaf)];
  const isWorking = Boolean(activity?.working);
  const roleEnabled = leaf.roleEnabled ?? leaf.enabled;
  const streamEnabled = leaf.streamEnabled ?? true;
  const syncStatus = leaf.syncState?.status || 'pending_sync';
  const syncBadgeVariant =
    syncStatus === 'in_sync'
      ? 'success'
      : syncStatus === 'error'
        ? 'destructive'
        : syncStatus === 'syncing'
          ? 'secondary'
          : 'warning';
  const syncing = syncingRoleKeys?.has(getRoleKey(leaf)) || syncStatus === 'syncing';

  return (
    <div className="flex flex-col items-center gap-4">
      <Card
        className={cn(
          'w-[240px] border',
          isLeafCard
            ? leafSurf?.className
            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900',
        )}
        style={isLeafCard ? leafSurf?.style : undefined}
      >
        <CardHeader className={cn('px-3', cardLayout === 'lite' ? 'pb-1 pt-2.5' : 'pb-2 pt-3')}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words whitespace-normal leading-snug">
                {leaf.agentTitle || leaf.tenantDisplayName || leaf.code}
              </h3>
              {isWorking ? (
                <button
                  type="button"
                  className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-300"
                  onClick={() => onOpenActiveChats?.(leaf)}
                  title={`Active chats: ${activity?.activeChats || 0}, active missions: ${activity?.activeMissions || 0}`}
                >
                  <Radio className="h-3 w-3 animate-pulse" />
                  Working
                </button>
              ) : null}
              {cardLayout === 'full' && (
                <RoleScopeChips
                  role={leaf}
                  definitionLabelMaps={definitionLabelMaps}
                  density="hierarchy"
                  className="mt-1 max-w-[212px]"
                />
              )}
            </div>
            <div className="flex items-start justify-end gap-0.5 shrink-0">
              {canManage ? (
                <DropdownMenu
                  align="end"
                  trigger={
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" aria-label="Agent actions">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  }
                  items={[
                    ...(canChat && onChatRole && leaf.code
                      ? [
                          {
                            label: 'Chat with role',
                            icon: <MessageSquare className="w-4 h-4" />,
                            onClick: () => onChatRole(leaf),
                          },
                        ]
                      : []),
                    {
                      label: 'Edit role config',
                      icon: <Edit className="w-4 h-4" />,
                      onClick: () => onEditRole(leaf),
                    },
                    ...(onListeners
                      ? [
                          {
                            label: 'Configure Listeners',
                            icon: <Radio className="w-4 h-4" />,
                            onClick: () => onListeners(leaf),
                          },
                        ]
                      : []),
                    ...(onSchedules
                      ? [
                          {
                            label: 'Scheduled jobs',
                            icon: <Clock className="w-4 h-4" />,
                            onClick: () => onSchedules(leaf),
                          },
                        ]
                      : []),
                    ...(onReconcile
                      ? [
                          {
                            label: 'Reconcile resources',
                            icon: <RefreshCw className="w-4 h-4" />,
                            onClick: () => onReconcile(leaf),
                          },
                        ]
                      : []),
                    ...(onSyncRole
                      ? [
                          {
                            label: syncing ? 'Syncing...' : 'Sync role artifacts',
                            icon: <RefreshCw className="w-4 h-4" />,
                            onClick: () => onSyncRole(leaf),
                          },
                        ]
                      : []),
                    ...(onSetParent
                      ? [
                          {
                            label: 'Link to Parent',
                            icon: <ChevronRight className="w-4 h-4" />,
                            onClick: () => onSetParent(leaf),
                          },
                        ]
                      : []),
                    {
                      label: roleEnabled ? 'Pause Role' : 'Resume Role',
                      icon: roleEnabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />,
                      onClick: () => onPauseToggle(leaf),
                    },
                    {
                      label: 'Delete',
                      icon: <Trash2 className="w-4 h-4" />,
                      onClick: () => onDelete(leaf),
                      className: 'text-red-600 dark:text-red-400',
                    },
                  ]}
                />
              ) : canChat && onChatRole && leaf.code ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 shrink-0"
                  aria-label="Chat with role"
                  onClick={() => onChatRole(leaf)}
                >
                  <MessageSquare className="w-4 h-4" />
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-3 pb-3 pt-0">
          {cardLayout === 'lite' ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="text-[10px] font-medium">
                {leaf.streamDisplayName || leaf.streamId}
              </Badge>
              <Badge variant={roleEnabled ? 'success' : 'warning'}>{roleEnabled ? 'Role active' : 'Role paused'}</Badge>
              <Badge variant={syncBadgeVariant}>{syncStatus.replace('_', ' ')}</Badge>
              {!streamEnabled ? <Badge variant="warning">Stream paused</Badge> : null}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <Badge variant={roleEnabled ? 'success' : 'warning'}>{roleEnabled ? 'Role active' : 'Role paused'}</Badge>
                <Badge variant={syncBadgeVariant}>{syncStatus.replace('_', ' ')}</Badge>
                {!streamEnabled ? <Badge variant="warning">Stream paused</Badge> : null}
                {leaf.code ? (
                  <Badge variant="outline" className="text-[11px] font-mono">{leaf.code}</Badge>
                ) : null}
              </div>
              {onSyncRole ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2 h-7 px-2 text-[11px]"
                  disabled={syncing}
                  onClick={() => onSyncRole(leaf)}
                >
                  {syncing ? 'Syncing…' : 'Sync role'}
                </Button>
              ) : null}
              {externalParentLabel && (
                <div className="mt-1.5 text-[10px] text-gray-400 dark:text-gray-500 truncate" title={`Parent: ${externalParentLabel}`}>
                  ↑ {externalParentLabel}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {children.length > 0 && (
        <div className="relative pt-5">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-6 bg-gray-300 dark:bg-gray-600" />
          {children.length > 1 && (
            <div className="absolute top-6 left-6 right-6 h-px bg-gray-300 dark:bg-gray-600" />
          )}
          <div
            className={cn(
              'pt-6',
              allSiblingsAreLeaves
                ? 'grid justify-center justify-items-center gap-x-4 gap-y-6'
                : 'flex flex-wrap justify-center gap-x-4 gap-y-6',
            )}
            style={
              allSiblingsAreLeaves
                ? { gridTemplateColumns: `repeat(${leafGridCols}, 240px)` }
                : undefined
            }
          >
            {children.map((childKey) => (
              <div key={childKey} className="relative flex shrink-0 flex-col items-center pt-5">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-5 bg-gray-300 dark:bg-gray-600" />
                <HierarchyNode
                  nodeKey={childKey}
                  rolesByKey={rolesByKey}
                  childrenByParent={childrenByParent}
                  definitionLabelMaps={definitionLabelMaps}
                  cardLayout={cardLayout}
                  canManage={canManage}
                  canChat={canChat}
                  onChatRole={onChatRole}
                  onEditRole={onEditRole}
                  onPauseToggle={onPauseToggle}
                  onDelete={onDelete}
                  onListeners={onListeners}
                  onSchedules={onSchedules}
                  onReconcile={onReconcile}
                  onSyncRole={onSyncRole}
                  onSetParent={onSetParent}
                  roleActivity={roleActivity}
                  syncingRoleKeys={syncingRoleKeys}
                  onOpenActiveChats={onOpenActiveChats}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** 10% per zoom in/out click or ctrl+wheel step */
const ZOOM_STEP = 1.1;
const MIN_ZOOM_BIAS = 0.2;
const MAX_ZOOM_BIAS = 6;
const VIEWPORT_PAD = 24;

export function AgentHierarchyView({
  roles,
  allRoles,
  definitionLabelMaps,
  canManage,
  canChat,
  onChatRole,
  onEditRole,
  onPauseToggle,
  onDelete,
  onListeners,
  onSchedules,
  onReconcile,
  onSyncRole,
  onSetParent,
  roleActivity,
  syncingRoleKeys,
  onOpenActiveChats,
  className,
  initialCardLayout = 'lite',
}: AgentHierarchyViewProps) {
  const [cardLayout, setCardLayout] = useState<HierarchyCardLayout>(initialCardLayout);
  const [linkTarget, setLinkTarget] = useState<RoleSummary | null>(null);
  const [selectedParentRef, setSelectedParentRef] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);

  const viewportRef = useRef<HTMLDivElement>(null);
  const contentMeasureRef = useRef<HTMLDivElement>(null);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [viewportSize, setViewportSize] = useState({ w: 0, h: 0 });
  /** User multiplier on top of auto “fit” scale (1 = fit when possible). */
  const [zoomBias, setZoomBias] = useState(1);
  const panRef = useRef<{ active: boolean; startX: number; startY: number; sl: number; st: number } | null>(null);

  const buildSource = allRoles ?? roles;
  const visibleKeys = useMemo(() => new Set(roles.map(getRoleKey)), [roles]);
  const isFiltered = allRoles !== undefined && allRoles.length !== roles.length;

  const {
    roots,
    unconnected,
    rolesByKey,
    childrenByParent,
    externalParents,
  } = useMemo(() => {
    const map = new Map<string, RoleSummary>();
    for (const r of buildSource) map.set(getRoleKey(r), r);

    const children = new Map<string, string[]>();
    const nodesWithParent = new Set<string>();
    const invalidParentRefs = new Set<string>();
    const extParents = new Map<string, string>();

    for (const r of buildSource) {
      const key = getRoleKey(r);
      const parent = resolveParentKey(r.parentRef, r.streamId, map);
      if (!parent) continue;

      if (!map.has(parent) || parent === key) {
        invalidParentRefs.add(key);
        continue;
      }

      const siblings = children.get(parent) || [];
      siblings.push(key);
      children.set(parent, siblings);
      nodesWithParent.add(key);
    }

    const allRootKeys = buildSource
      .map((r) => getRoleKey(r))
      .filter((key) => !nodesWithParent.has(key) && !invalidParentRefs.has(key));

    const visited = new Set<string>();
    const walk = (key: string) => {
      if (visited.has(key)) return;
      visited.add(key);
      for (const child of children.get(key) || []) walk(child);
    };
    for (const root of allRootKeys) walk(root);

    const allUnconnected = buildSource
      .map((r) => getRoleKey(r))
      .filter((key) => invalidParentRefs.has(key) || !visited.has(key));

    if (isFiltered) {
      const visibleRoots: string[] = [];
      const visibleUnconnected: string[] = [];

      for (const key of [...allRootKeys, ...allUnconnected]) {
        if (visibleKeys.has(key)) {
          if (allUnconnected.includes(key)) visibleUnconnected.push(key);
          else visibleRoots.push(key);
        }
      }

      for (const r of roles) {
        const key = getRoleKey(r);
        if (visibleRoots.includes(key) || visibleUnconnected.includes(key)) continue;
        const parentKey = resolveParentKey(r.parentRef, r.streamId, map);
        if (parentKey && !visibleKeys.has(parentKey)) {
          visibleRoots.push(key);
          const parentRole = map.get(parentKey);
          if (parentRole) {
            extParents.set(key, `${parentRole.agentTitle || parentRole.tenantDisplayName} (${parentRole.streamId})`);
          }
        }
      }

      const filteredChildren = new Map<string, string[]>();
      for (const [parent, kids] of children) {
        const visKids = kids.filter((k) => visibleKeys.has(k));
        if (visKids.length > 0) filteredChildren.set(parent, visKids);
      }

      return {
        roots: visibleRoots,
        unconnected: visibleUnconnected,
        rolesByKey: map,
        childrenByParent: filteredChildren,
        externalParents: extParents,
      };
    }

    return {
      roots: allRootKeys,
      unconnected: allUnconnected,
      rolesByKey: map,
      childrenByParent: children,
      externalParents: extParents,
    };
  }, [buildSource, roles, visibleKeys, isFiltered]);

  const hierarchyTreeKey = useMemo(
    () => `${[...roots].sort().join(',')}||${[...unconnected].sort().join(',')}`,
    [roots, unconnected],
  );

  const parentCandidates = useMemo(() => {
    if (!linkTarget) return [];
    const targetKey = getRoleKey(linkTarget);
    return roles
      .filter((leaf) => getRoleKey(leaf) !== targetKey)
      .map((leaf) => ({
        value: getRoleKey(leaf),
        label: `${leaf.agentTitle || leaf.tenantDisplayName || leaf.code} (${getRoleKey(leaf)})`,
      }));
  }, [roles, linkTarget]);

  const openLinkDialog = (leaf: RoleSummary) => {
    setLinkTarget(leaf);
    setSelectedParentRef(leaf.parentRef || '');
  };

  const handleSaveLink = async () => {
    if (!linkTarget || !onSetParent) return;
    setLinkSaving(true);
    try {
      await onSetParent(linkTarget, selectedParentRef || undefined);
      setLinkTarget(null);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to update parent link');
    } finally {
      setLinkSaving(false);
    }
  };

  const measureContent = useCallback(() => {
    const el = contentMeasureRef.current;
    if (!el) return;
    // scrollWidth/scrollHeight track the laid-out content box; offset* can include extra slack
    // with w-max/flex trees and makes the zoom spacer too tall (large empty bottom margin).
    const w = Math.ceil(Math.max(el.scrollWidth, el.offsetWidth));
    const sh = el.scrollHeight;
    const oh = el.offsetHeight;
    const h = Math.ceil(
      sh > 0 && oh > 0 ? Math.min(sh, oh) : Math.max(sh, oh, 1),
    );
    if (w > 0 && h > 0) {
      setNaturalSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    }
  }, []);

  useLayoutEffect(() => {
    measureContent();
    const el = contentMeasureRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measureContent());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureContent, hierarchyTreeKey, cardLayout]);

  useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const ro = new ResizeObserver(() => {
      setViewportSize({ w: vp.clientWidth, h: vp.clientHeight });
    });
    ro.observe(vp);
    setViewportSize({ w: vp.clientWidth, h: vp.clientHeight });
    return () => ro.disconnect();
  }, []);

  /** Fit to viewport width (not height); vertical overflow scrolls. */
  const fitScale = useMemo(() => {
    if (!naturalSize.w || !viewportSize.w) return 1;
    const vw = Math.max(1, viewportSize.w - VIEWPORT_PAD);
    return Math.min(1, vw / naturalSize.w);
  }, [naturalSize.w, viewportSize.w]);

  const displayScale = useMemo(
    () => Math.min(3, Math.max(0.08, fitScale * zoomBias)),
    [fitScale, zoomBias],
  );

  const spacerW = naturalSize.w > 0 ? naturalSize.w * displayScale : 0;
  const spacerH = naturalSize.h > 0 ? naturalSize.h * displayScale : 0;

  const scrollToFitCenter = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp || !spacerW || !spacerH) return;
    const cx = Math.max(0, (spacerW - vp.clientWidth) / 2);
    const cy = Math.max(0, (spacerH - vp.clientHeight) / 2);
    vp.scrollTo({ left: cx, top: cy, behavior: 'smooth' });
  }, [spacerW, spacerH]);

  useEffect(() => {
    setZoomBias(1);
  }, [hierarchyTreeKey]);

  const handleFit = useCallback(() => {
    setZoomBias(1);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => scrollToFitCenter());
    });
  }, [scrollToFitCenter]);

  const handleZoomIn = useCallback(() => {
    setZoomBias((z) => Math.min(MAX_ZOOM_BIAS, z * ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomBias((z) => Math.max(MIN_ZOOM_BIAS, z / ZOOM_STEP));
  }, []);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP;
      setZoomBias((z) => Math.min(MAX_ZOOM_BIAS, Math.max(MIN_ZOOM_BIAS, z * delta)));
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!e.altKey || e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('button,a,input,select,textarea,[role="menuitem"]')) return;
      panRef.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        sl: vp.scrollLeft,
        st: vp.scrollTop,
      };
      vp.setPointerCapture(e.pointerId);
      vp.classList.add('cursor-grabbing');
    };

    const onPointerMove = (e: PointerEvent) => {
      const p = panRef.current;
      if (!p?.active) return;
      vp.scrollLeft = p.sl + (p.startX - e.clientX);
      vp.scrollTop = p.st + (p.startY - e.clientY);
    };

    const endPan = (e: PointerEvent) => {
      if (!panRef.current?.active) return;
      panRef.current = null;
      try {
        vp.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      vp.classList.remove('cursor-grabbing');
    };

    vp.addEventListener('pointerdown', onPointerDown);
    vp.addEventListener('pointermove', onPointerMove);
    vp.addEventListener('pointerup', endPan);
    vp.addEventListener('pointercancel', endPan);
    return () => {
      vp.removeEventListener('pointerdown', onPointerDown);
      vp.removeEventListener('pointermove', onPointerMove);
      vp.removeEventListener('pointerup', endPan);
      vp.removeEventListener('pointercancel', endPan);
    };
  }, []);

  const hierarchyTree = (
    <div className="flex flex-wrap justify-center gap-6">
      {roots.map((rootKey) => (
        <HierarchyNode
          key={rootKey}
          nodeKey={rootKey}
          rolesByKey={rolesByKey}
          childrenByParent={childrenByParent}
          definitionLabelMaps={definitionLabelMaps}
          cardLayout={cardLayout}
          canManage={canManage}
          canChat={canChat}
          onChatRole={onChatRole}
          onEditRole={onEditRole}
          onPauseToggle={onPauseToggle}
          onDelete={onDelete}
          onListeners={onListeners}
          onSchedules={onSchedules}
          onReconcile={onReconcile}
          onSyncRole={onSyncRole}
          onSetParent={onSetParent ? openLinkDialog : undefined}
          roleActivity={roleActivity}
          syncingRoleKeys={syncingRoleKeys}
          onOpenActiveChats={onOpenActiveChats}
          externalParentLabel={externalParents.get(rootKey)}
        />
      ))}
    </div>
  );

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col gap-4', className)}>
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-gray-100/60 dark:border-gray-700 dark:bg-gray-950/50">
        <div className="absolute top-2 right-2 z-20 flex items-center gap-0.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 p-1 shadow-sm backdrop-blur-sm">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={handleZoomOut}
            aria-label="Zoom out"
            title="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-[11px] tabular-nums text-gray-600 dark:text-gray-400 min-w-[2.75rem] text-center">
            {Math.round(displayScale * 100)}%
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={handleZoomIn}
            aria-label="Zoom in"
            title="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={handleFit}
            aria-label="Fit to view"
            title="Fit to view"
          >
            <Maximize2 className="w-4 h-4" />
          </Button>
          <div className="mx-0.5 h-5 w-px shrink-0 bg-gray-200 dark:bg-gray-600" aria-hidden />
          <Button
            type="button"
            variant={cardLayout === 'lite' ? 'default' : 'outline'}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setCardLayout((c) => (c === 'lite' ? 'full' : 'lite'))}
            aria-label={cardLayout === 'lite' ? 'Show detailed cards' : 'Show compact cards'}
            title={cardLayout === 'lite' ? 'Detailed cards' : 'Compact cards'}
          >
            {cardLayout === 'lite' ? <LayoutGrid className="w-4 h-4" /> : <List className="w-4 h-4" />}
          </Button>
        </div>
        <p className="pointer-events-none absolute bottom-2 left-3 z-10 text-[10px] text-gray-500 dark:text-gray-500">
          Scroll to pan · Alt+drag to pan · Ctrl+scroll to zoom
        </p>

        <div
          ref={viewportRef}
          className="min-h-0 flex-1 overflow-auto cursor-default"
          title="Alt+drag to pan. Ctrl or ⌘ + scroll wheel to zoom."
        >
          <div
            className="relative"
            style={{
              width: naturalSize.w > 0 ? spacerW : undefined,
              height: naturalSize.h > 0 ? spacerH : undefined,
              minHeight: naturalSize.w > 0 ? undefined : 80,
              display: naturalSize.w > 0 ? 'block' : 'inline-block',
            }}
          >
            <div
              ref={contentMeasureRef}
              className="w-max min-w-max p-4"
              style={
                naturalSize.w > 0
                  ? {
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      transform: `scale(${displayScale})`,
                      transformOrigin: 'top left',
                    }
                  : undefined
              }
            >
              {hierarchyTree}
            </div>
          </div>
        </div>
      </div>

      {unconnected.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Unconnected</h3>
          <div className="flex flex-wrap gap-4">
            {unconnected.map((key) => {
              const leaf = rolesByKey.get(key);
              if (!leaf) return null;
              return (
                <div key={key} className="w-[240px]">
                  <HierarchyNode
                    nodeKey={key}
                    rolesByKey={rolesByKey}
                    childrenByParent={new Map()}
                    definitionLabelMaps={definitionLabelMaps}
                    cardLayout={cardLayout}
                    canManage={canManage}
                    canChat={canChat}
                    onChatRole={onChatRole}
                    onEditRole={onEditRole}
                    onPauseToggle={onPauseToggle}
                    onDelete={onDelete}
                    onListeners={onListeners}
                    onSchedules={onSchedules}
                    onReconcile={onReconcile}
                    onSyncRole={onSyncRole}
                    onSetParent={onSetParent ? openLinkDialog : undefined}
                    roleActivity={roleActivity}
                    syncingRoleKeys={syncingRoleKeys}
                    onOpenActiveChats={onOpenActiveChats}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={!!linkTarget} onOpenChange={(open) => !open && setLinkTarget(null)}>
        <DialogContent maxWidth="lg">
          <DialogHeader>
            <DialogTitle>Link Parent Agent</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            {linkTarget && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Child: <span className="font-medium">{linkTarget.agentTitle || linkTarget.tenantDisplayName}</span>
              </p>
            )}
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400">Parent Agent</label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 text-sm"
                value={selectedParentRef}
                onChange={(e) => setSelectedParentRef(e.target.value)}
              >
                <option value="">None</option>
                {parentCandidates.map((candidate) => (
                  <option key={candidate.value} value={candidate.value}>
                    {candidate.label}
                  </option>
                ))}
              </select>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setLinkTarget(null)} disabled={linkSaving}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleSaveLink()} disabled={linkSaving}>
              {linkSaving ? 'Saving…' : 'Save Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
